import { z } from 'zod';
import { UsStateCodeSchema } from './usStateCode.constants';

/** Customer response DTO (warehouse name + optional state settings). */
export interface ReportCustomerDto {
  id: string;
  /** Warehouse `customer` label; synced from BigQuery. */
  name: string;
  /** Two-letter state for Who We're Reaching county maps; null until configured. */
  usStateCode: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Request body for updating report customer settings. */
export const UpdateReportCustomerSettingsSchema = z
  .object({
    usStateCode: UsStateCodeSchema.nullable(),
  })
  .strict();

/** Parsed update-customer-settings request. */
export type UpdateReportCustomerSettingsDto = z.infer<
  typeof UpdateReportCustomerSettingsSchema
>;

/** Optional query when listing report customers. */
export const ListReportCustomersQuerySchema = z.object({
  /** When true, sync customer names from BigQuery before returning Postgres rows. */
  syncFromWarehouse: z
    .enum(['true', 'false'])
    .optional()
    .transform(value => value === 'true'),
});

/** Parsed list-report-customers query. */
export type ListReportCustomersQueryDto = z.infer<
  typeof ListReportCustomersQuerySchema
>;

/** Optional filter when listing reports. */
export const ListReportsQuerySchema = z.object({
  customerId: z.string().uuid().optional(),
});

/** Parsed list-reports query. */
export type ListReportsQueryDto = z.infer<typeof ListReportsQuerySchema>;

/** Optional filter when listing templates. */
export const ListTemplatesQuerySchema = z.object({
  customerId: z.string().uuid().optional(),
});

/** Parsed list-templates query. */
export type ListTemplatesQueryDto = z.infer<typeof ListTemplatesQuerySchema>;

/** One measurement period option for report create/edit pickers. */
export interface ReportMeasurementPeriodOptionDto {
  /** Warehouse `measurement_period` value (stored on the report). */
  value: string;
  /** Human-readable label for dropdown display. */
  label: string;
}

/** One reporting month option for monthly/quarterly period pickers. */
export interface ReportReportingMonthOptionDto {
  /** `YYYY-MM` key for month pickers. */
  value: string;
  /** Display label such as `Apr 2026`. */
  label: string;
}
